import type { ConversationMessage } from '@/features/AI-documents/types/docGen.types';
import type { ChatFlow, ChatFlowContext, ChatFlowEventResult } from '@/features/startTask/preset_task/shared/preset/chatflows/types';

export const noticeIndividualControllerFlow: ChatFlow = {
  id: 'sg-controller-notice-individual',
  taskCodeMatch: ['B32106'],
  categoryMatch: [
    'notice for controller',
    'notice for controllers',
    'controller notice individual',
    'notice controller individual',
    'controller (individual)',
    '386ag individual',
    '386ag(2)(a) individual',
    'individual controller notice',
  ],

  getInitialMessages(preSet): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `You selected **${preSet.task.TASK_NAME}** (Notice for Controllers 386AG(2)(a) — Individual).\n\n` +
          (docCount > 0 ? `This preset includes ${docCount} document(s).\n\n` : '') +
          `Enter a **Task Title**, select the **Singapore company**, then complete the notice and individual controller details on the right.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: {
        targetVarKey: '_taskTitle',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage:
        'Search and select the **Singapore company** issuing this controller notice.',
      widget: 'entity-selector',
      widgetConfig: {
        targetVarKey: '_entity',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Entity linked. Fill in notice date, reply address, and individual controller details on the right. Use **Lookup from chat** to pick the controller contact. Click **Create Task** when ready.',
      widget: null,
      nextStep: null,
    },
    {
      id: 'select-controller',
      aiMessage: 'Select a **global contact** for this notice (recipient / controller).',
      widget: 'officer-list',
      widgetConfig: {
        targetVarKey: '_controller',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'select-secretary',
      aiMessage: 'Select the company **secretary** for notice signer.',
      widget: 'officer-list',
      widgetConfig: {
        targetVarKey: '_secretary',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'address-list',
      aiMessage: 'This entity has multiple addresses. Pick the one to use for **Reply-to address**.',
      widget: 'address-options',
      awaitEvent: true,
    },
    {
      id: 'address-input',
      aiMessage: 'Please enter the **Reply-to address** (Singapore). It will be filled back to the form automatically.',
      widget: 'address-input',
      awaitEvent: true,
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    if (eventName === 'value-submit' && context.currentStepId === 'input-title') {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [
          { role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 },
        ],
        formUpdates: { taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = ent.entityCd || ent.icpCd || '';
      const entityName = ent.entityName || ent.companyName || 'Entity';
      if (!entityCd) return {};
      return {
        messages: [
          {
            role: 'user',
            content: `Selected: **${entityName}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          entityCd,
          entityName,
          icpCd: entityCd,
          registrationNo: ent.registrationNo || ent.regNo || '',
          companyAddress: ent.companyAddress || ent.address || '',
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const fg = String(ctx.fieldGroup || '');

      const entityCd = context.entityCd || context.formData.entityCd || context.formData.icpCd || '';
      if (!entityCd) {
        return {
          messages: [
            {
              role: 'ai',
              content: 'Please select an entity first.',
              timestamp: Date.now(),
              stepId: 1,
              source: 'system',
            },
          ],
        };
      }

      if (fg === 'noticeSigner') {
        return {
          messages: [
            {
              role: 'user',
              content: 'Looking up **notice signer** (company secretary)...',
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'select-secretary',
          nextStepWidgetData: {
            entityCd,
            selectedRole: ctx.defaultRole || 'SECRETARY',
          },
        };
      }

      if (fg !== 'individualController' && fg !== 'noticeRecipient') return {};

      const lookupLabel =
        fg === 'noticeRecipient' ? 'notice recipient (global contact)' : 'individual controller';

      return {
        messages: [
          {
            role: 'user',
            content: `Looking up **${lookupLabel}**...`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'select-controller',
        nextStepWidgetData: {
          entityCd,
          selectedRole: ctx.defaultRole || 'CONTACT',
        },
      };
    }

    if (eventName === 'request-address-lookup') {
      const ctx = payload || {};
      const fg = String(ctx.fieldGroup || '');

      if (fg === 'controllerResidentialAddress' || fg === 'controllerAlternateAddress') {
        const label = ctx.subjectLabel || (fg === 'controllerAlternateAddress'
          ? 'Alternate address'
          : 'Residential address');
        if (ctx.directInput) {
          return {
            messages: [
              {
                role: 'user',
                content: `Enter **${label}** (Local Singapore or Foreign).`,
                timestamp: Date.now(),
                stepId: 1,
              },
            ],
            nextStepId: 'address-input',
          };
        }
        const opts = Array.isArray(ctx.addresses) ? ctx.addresses : [];
        if (opts.length <= 1) {
          return {
            messages: [
              {
                role: 'user',
                content: `Enter **${label}** (Local Singapore or Foreign).`,
                timestamp: Date.now(),
                stepId: 1,
              },
            ],
            nextStepId: 'address-input',
          };
        }
        return {
          messages: [
            {
              role: 'user',
              content: `Choose an address for **${label}**.`,
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'address-list',
          nextStepWidgetData: { addressOptions: opts },
        };
      }

      if (fg !== 'replyToAddress') return {};

      if (ctx.directInput) {
        return {
          messages: [
            {
              role: 'user',
              content: 'Enter **Reply-to address** (Singapore registered address format).',
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'address-input',
        };
      }

      const opts = Array.isArray(ctx.addresses) ? ctx.addresses : [];
      if (opts.length <= 1) {
        return {
          messages: [
            {
              role: 'user',
              content: 'Enter **Reply-to address** (Singapore).',
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'address-input',
        };
      }
      return {
        messages: [
          {
            role: 'user',
            content: `Choose an address for **${ctx.subjectLabel || 'Reply-to address'}**.`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'address-list',
        nextStepWidgetData: { addressOptions: opts },
      };
    }

    if (eventName === 'entity-field-select' && context.currentStepId === 'address-input') {
      const value = typeof payload === 'string' ? payload : payload?.value || payload?.address || '';
      if (!value || !String(value).trim()) return {};
      return {
        messages: [
          {
            role: 'user',
            content: `Entered: **${String(value).trim()}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'address-option-select') {
      const opt = payload || {};
      return {
        messages: [
          {
            role: 'user',
            content: `Selected: **${opt.label || opt.type || 'Address'}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'officer-select' && context.currentStepId === 'select-controller') {
      const officer = payload || {};
      const officerName = officer.name || officer.contactName || 'Selected controller';
      return {
        messages: [
          {
            role: 'user',
            content: `Selected controller: **${officerName}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'officer-select' && context.currentStepId === 'select-secretary') {
      const officer = payload || {};
      const officerName = officer.name || officer.contactName || 'Selected secretary';
      return {
        messages: [
          {
            role: 'user',
            content: `Selected secretary: **${officerName}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'confirm',
      };
    }

    return {};
  },
};
